import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Clock, Users, BookOpen, ArrowRight } from 'lucide-react';
import { Badge } from './ui/badge';

export default function CourseCard({ course }) {
  const badgeColors = {
    'Best Seller': 'bg-amber-100 text-amber-800 border-amber-200',
    'Popular': 'bg-blue-100 text-blue-800 border-blue-200',
    'Trending': 'bg-pink-100 text-pink-800 border-pink-200',
    'New': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'Hot': 'bg-red-100 text-red-800 border-red-200',
    'Best Value': 'bg-purple-100 text-purple-800 border-purple-200',
    'Emerging': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  };

  return (
    <Link to={`/course/${course.id}`} className="group block">
      <div className="tilt-card course-card-shine bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-purple-200 shadow-sm hover:shadow-2xl h-full flex flex-col">
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={course.thumbnail}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className={`absolute inset-0 bg-gradient-to-tr ${course.color} mix-blend-multiply opacity-50 group-hover:opacity-30 transition-opacity`} />
          <div className="absolute top-3 left-3 flex gap-2">
            {course.badge && (
              <span className={`text-[11px] font-bold px-2.5 py-1 rounded-md border ${badgeColors[course.badge] || 'bg-white text-gray-800'}`}>
                {course.badge}
              </span>
            )}
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-md bg-white/95 text-gray-700 backdrop-blur">
              {course.level}
            </span>
          </div>
          <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur text-white text-xs font-semibold px-2 py-1 rounded-md flex items-center gap-1">
            <Clock size={12} /> {course.duration}
          </div>
        </div>

        <div className="p-5 flex-1 flex flex-col">
          <h3 className="font-bold text-[17px] leading-snug text-gray-900 group-hover:text-[#6b47dc] transition-colors line-clamp-2 mb-1">
            {course.title}
          </h3>
          <p className="text-xs text-gray-500 mb-3">{course.tagline}</p>

          <div className="flex flex-wrap gap-1.5 mb-3">
            {course.skills.slice(0, 3).map(s => (
              <span key={s} className="text-[10px] font-semibold px-2 py-0.5 rounded bg-purple-50 text-[#6b47dc]">{s}</span>
            ))}
            {course.skills.length > 3 && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-600">+{course.skills.length - 3}</span>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-gray-600 mb-3">
            <span className="flex items-center gap-1 font-semibold text-amber-600">
              <Star size={13} className="fill-amber-500 text-amber-500" /> {course.rating}
            </span>
            <span className="text-gray-400">({course.reviews.toLocaleString()})</span>
            <span className="flex items-center gap-1">
              <Users size={12} /> {(course.students / 1000).toFixed(1)}k
            </span>
          </div>

          <div className="mt-auto flex items-end justify-between pt-3 border-t border-gray-100">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-extrabold text-gray-900">${course.discountPrice.toLocaleString()}</span>
                <span className="text-xs text-gray-400 line-through">${course.price.toLocaleString()}</span>
              </div>
              <div className="text-[11px] font-semibold text-emerald-600">
                {Math.round(((course.price - course.discountPrice) / course.price) * 100)}% off
              </div>
            </div>
            <div className="text-[#6b47dc] group-hover:translate-x-1 transition-transform">
              <ArrowRight size={20} />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
